import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Amenity } from '@models/interfaces/amenities/Amenity.interface';
import { AmenityApi } from '@models/interfaces/amenities/AmenityApi.interface';
import { AmenityClient, AmsApiClient } from '@models/interfaces/amenities/AmenityClient.interface';
import { AmenityGroup, AmsApiGroup, AmsApiGroups } from '@models/interfaces/amenities/AmenityGroup.interface';
import { map, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AmsEndpoints, AmsRequestType } from '@models/enums/amenities/ApiEndpoints.enum';
import { AmsApiGroupAssigned, AmsApiGroupsAssigned, GroupAssignedAmenity } from '@models/interfaces/amenities/AmenityGroupAssigned.interface';
import { FilterService } from './filter.service';

@Injectable({providedIn: 'root'})
export class AmenitiesService {

  // SERVICES
  private readonly api:           AmenityApi    = environment.amenityApi;
  private readonly http:          HttpClient    = inject(HttpClient);
  private readonly filterService: FilterService = inject(FilterService);

  
  // BASE URLS
  private readonly privateUrl:  string     = `${this.api.baseUrl}/${AmsRequestType.private}/${this.api.version}`;
  private readonly publicUrl:   string     = `${this.api.baseUrl}/${AmsRequestType.public}/${this.api.version}`;


  // STATE
  public  readonly amsMap:                Map<number, GroupAssignedAmenity>      = new Map<number, GroupAssignedAmenity>();
  public  readonly selectedAmenities:     WritableSignal<GroupAssignedAmenity[]> = signal<GroupAssignedAmenity[]>([]);
  public  readonly hasSelectedAmenities:  Signal<boolean>                        = computed((): boolean => this.selectedAmenities().length > 0);
  // For detecting a change in amsMap, we need a Subject instead. Signals dont work well with Maps, .set and .update dont trigger updates.
  public  readonly amsMapLoaded$:         Subject<boolean>                       = new Subject<boolean>();

  // METHODS
  public checkSeatAmenity(nodeId: string): void {

    const amenitiesFound: GroupAssignedAmenity[] = this.getAmenitiesBySeat(nodeId);

    if(amenitiesFound.length) {

      this.updateSelectedAmenities(amenitiesFound)

    }
    else if(this.selectedAmenities().length) {

      this.resetSelectedAmenities();
      
    } 

  }

  public checkSectionAmenity(sectionId: string): void {

    if(this.amsMap.size === 0 || this.filterService.hasFilters) return;

    const amsFound: GroupAssignedAmenity[] = [];

    for(const amenity of this.amsMap.values()) {

      const sectionsData: string[] = amenity.relatedSeats.split(';').map((str: string) => str.split('-')[0]);
    
      if(sectionsData.includes(sectionId)) amsFound.push(amenity);

    }

    if(amsFound.length) {

      this.updateSelectedAmenities(amsFound);

    }
    else if(this.selectedAmenities().length) {

      this.resetSelectedAmenities();

    }

  }

  public getAmenitiesBySeat(nodeId: string): GroupAssignedAmenity[] {
   
    if(this.amsMap.size === 0) return [];

    return Array.from(this.amsMap.values()).filter((amenity: GroupAssignedAmenity): boolean => amenity.relatedSeats.includes(nodeId));

  }

  public setAmenityFromMenu(amenity: GroupAssignedAmenity): void {

    // Si no hay amenities seleccionadas, se añade la nueva amenity
    if(!this.selectedAmenities().length) return this.updateSelectedAmenities(amenity);

    // Si la amenity ya está seleccionada, se resetea la selección
    if(this.selectedAmenities().some(v => v.id === amenity.id)) return this.resetSelectedAmenities();
    
    // Si hay un amenity y no es el mismo, se resetea y se añade la nueva amenity
    this.resetSelectedAmenities();
    this.updateSelectedAmenities(amenity);

  }

  public updateSelectedAmenities(amenities: GroupAssignedAmenity | GroupAssignedAmenity[]): void {

    this.selectedAmenities.set( Array.isArray(amenities) ? [...amenities] : [amenities])
      
  }

  public resetSelectedAmenities(): void {

    this.selectedAmenities.set([]);

  }

  // Given a sectionId, return true if section is in an amenity relatedSeats. 
  public sectionHasAmenity(sectionId: string, amenityId: number): boolean {
    for(const amenity of this.amsMap.values()) {
      const sectionsData: string[] = amenity.relatedSeats.split(';').map((str: string) => str.split('-')[0]);
      if(sectionsData.includes(sectionId) && amenity.id === amenityId) return true;
    }
    return false;
  }

  // API METHODS
  public getClients(active?: boolean): Observable<AmenityClient[]> {

    const endpoint: string = `${this.privateUrl}/${AmsEndpoints.client}/${active ? 'active/' : ''}`;

    return this.http
      .get<AmsApiClient>(endpoint)
      .pipe(map((res) => res.data.entities));

  }

  public getGroups(clientId: string): Observable<AmenityGroup[]> {

    const endpoint: string = `${this.privateUrl}/${AmsEndpoints.client}/${clientId}/${AmsEndpoints.groups}/`;
    
    return this.http
      .get<AmsApiGroups>(endpoint)
      .pipe(map((res) => res.data));

  }

  public getAmenities(groupSlug: string): Observable<Amenity[]> {
    
    const endpoint: string     = `${this.privateUrl}/${AmsEndpoints.group}/${groupSlug}`,
          params:   HttpParams = new HttpParams().set('relations', 'amenities');

    return this.http
      .get<AmsApiGroup>(endpoint, { params })
      .pipe(map((res) => res.data.amenities));

  }

  public searchAmenities(id: string | string[]): Observable<GroupAssignedAmenity[]> {

    const endpoint: string = `${this.publicUrl}/${AmsEndpoints.amenities}`,
          params:   HttpParams = new HttpParams().set('search', Array.isArray(id) ? id.join(',') : id);

    return this.http
      .get<AmsApiGroupsAssigned>(endpoint, { params })
      .pipe(map((res) => res.data ?? [] as GroupAssignedAmenity[]));

  }

}